import { nanoid } from 'nanoid';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { subscribe, useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import SurfaceCanvas from '~/components2/Map/canvas/Surface';
import { mapState } from '~/components2/Map/Map';
import { useCivilDrawStore } from '../civilDrawStoreContext';

interface SurfaceProps {
  interactive?: boolean;
}

export default function Surface({ interactive = true }: SurfaceProps) {
  const store = useCivilDrawStore();
  const map = useMap();
  const containerDiv = useRef<HTMLDivElement>(null);
  const { undoStack, redoStack, currentObjectId, currentMapId, project } = useSnapshot(store) as typeof store;

  const platform = navigator.userAgent;
  const isMacOS = platform.toLowerCase().includes('mac'); // NOTE: does not check for iOS

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (currentObjectId) {
      store.previousObject = JSON.stringify(store.currentObject);
    } else {
      store.previousObject = undefined;
    }
  }, [currentObjectId]);

  /**
   * Purpose is to gather all initial objects on screen and immediately keep track of them.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const allInitialObjects = store.objectsOnMainSurface;
    const allKeys = Object.keys(allInitialObjects).map((key) => key);
    store.allObjectIds = allKeys;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const mapRef = map.current;
    if (!containerDiv.current || !mapRef) {
      return;
    }

    const surface = new SurfaceCanvas({
      id: `main-surface-${nanoid()}`,
      container: containerDiv.current,
      map: mapRef.getMap(),
      store: store,
    });

    if (interactive === false) {
      surface.objects.forEach((o) => {
        o.interactive = false;
      });
    }

    const unsubscribe = subscribe(mapState, () => {
      surface.refreshScreenCoords();
    });

    return () => {
      unsubscribe();
      surface.destroy();
    };
  }, [interactive, map]);

  const undoRedo = async (e: KeyboardEvent) => {
    // order of operation is important. the more keys to press, the earlier the if statement.
    if (e.key === 'z' && (isMacOS ? e.metaKey : e.ctrlKey) && e.shiftKey) {
      if (redoStack.length === 0) return;
      if (project.isLoading === false) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawActionRedone, {
          project_id: project.id,
          organization_id: project.organization || undefined,
          placement: 'key binding',
          ...(currentMapId && { map_id: currentMapId }),
        });
      }
      return store.redo();
    }
    if (e.key === 'z' && (isMacOS ? e.metaKey : e.ctrlKey)) {
      if (undoStack.length === 0) return;
      if (project.isLoading === false) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawActionUndone, {
          project_id: project.id,
          organization_id: project.organization || undefined,
          placement: 'key binding',
          ...(currentMapId && { map_id: currentMapId }),
        });
      }
      return store.undo();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    document.addEventListener('keydown', undoRedo);

    return () => {
      document.removeEventListener('keydown', undoRedo);
    };
  }, [undoStack, redoStack]);

  return <div style={{ cursor: '-moz-grab' }} className={'absolute left-0 top-0'} ref={containerDiv}></div>;
}
